import Vue from 'vue'
import { debounce } from 'debounce'
import { mapGetters } from 'vuex'
import Analytics from '@/services/Analytics'
import IList from '@/types/components/IList.interface'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import IListTile from '@/types/components/IListTile.interface'
import Invoice from '@/types/Invoice.interface'
import Message, { MessageSendType } from '@/types/Message.interface'
import { Phone } from '@/types/Person.interface'
import { InvoiceFormResponse } from '@/types/DataFormResponses.interface'
import Button from '@/types/components/Button.interface'
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import moment from 'moment-timezone'
export default Vue.extend({
    data() {
        return {
            //  Our filter for our invoices
            filter: {
                selected: {
                    display: 'Open',
                    key: 'filter',
                    value: 'open'
                },
                options: [
                    {
                        display: 'Newest to Oldest',
                        key: 'filter',
                        value: 'desc'
                    },
                    {
                        display: 'Oldest to Newest',
                        key: 'filter',
                        value: 'asc'
                    },
                    {
                        display: 'Open',
                        key: 'filter',
                        value: 'open'
                    },
                    {
                        display: 'Paid',
                        key: 'filter',
                        value: 'paid'
                    },
                    {
                        display: 'By Highest Balance',
                        key: 'filter',
                        value: 'highest_bal'
                    },
                    {
                        display: 'By Lowest Balance',
                        key: 'filter',
                        value: 'lowest_bal'
                    },
                    {
                        display: 'Overdue',
                        key: 'filter',
                        value: 'overdue'
                    },
                    {
                        display: 'Upcoming',
                        key: 'filter',
                        value: 'upcoming'
                    },
                    // {
                    //     display: 'Scheduled',
                    //     key: 'filter',
                    //     value: 'scheduled'
                    // },
                    {
                        display: 'Sent',
                        key: 'filter',
                        value: 'sent'
                    },
                    {
                        display: 'Not Sent',
                        key: 'filter',
                        value: 'not_sent'
                    }
                ]
            },
            invoiceMessage: null,
            invoiceMessages: [],
            //  Tracks if we are currently sending or doing something
            loading: false,
            recipient: null,
            sendDisabled: true,
            //  Determines if we are currently showing search results or not
            searched: false,
            //  Displays the status of our search
            searching: false,
            //  Holds a list of invoice search results
            searchResults: null,
            //  Our search text string
            searchtext: null,
        }
    },
    computed: {
        ...mapGetters([
            'defaultTemplate',
            'hasInvoices',
            'invoices',
            'isSphere',,
            'user'
        ]),
        createButtonVisible(): boolean {
            return (
                this.$store.getters.tenant.data_provider !== 'XERO' &&
                this.$store.getters.tenant.data_provider !== 'QBO'
            )
        },
        hasMobile(): boolean {
            return this.$store.getters.hasMobile(this.person)
        },
        operator(): boolean {
            //  Grab our current role
            const role = this.$store.getters.userRole(this.$store.getters.user)

            //  Check if this role is an operator
            return window.getProps(role, ['name']) === 'Operator'
        },
        search: {
            get(): string {
                return this.searchtext
            },
            set: debounce(function (value: string) {
                //  Set our search text
                this.searchtext = value
                //  Also search for any invoices matching the query
                this.invoicesSearch()
            }, 750)
        },
        //  True if we are in the person invoices because we do not want to display the profile
        single() {
            return !this.$route.path.includes('invoices')
        }
    },
    methods: {
        //  Checks if the balance of our invoice is positive meaning not paid
        balanceIsValid(invoice: Invoice): boolean {
            //  If invoice is NOT paid, then it is valid
            if (invoice.balance > 0) return true

            //  Else invoice is paid / balance is 0
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Invalid Invoice',
                text: "Invoices marked as 'paid' are not allowed to be sent",
                icon: 'mdi-alert',
                color: 'primary'
            })

            //  Not valid
            return false
        },
        // Only available if an invoice has a schedule attached
        cancelSchedule(invoice: Invoice) {
            //  Grab the message from the schedule on our invoice
            const msg = invoice.schedule.message

            //  Delete the message
            this.$store.dispatch('messagesDelete', msg)

            //  Set the schedule on this invoice to null
            invoice.schedule = null
        },
        description(invoice: Invoice): string {
            //  If the invoice balance is zero, return 'Paid'
            if (invoice.balance == 0) return 'Paid'

            //  If invoice has an active schedule
            if (invoice.schedule && !invoice.schedule.sent) {
                return `Scheduled for ${this.$options.filters.humanDate(invoice.schedule.next_send)}`
            }

            //  Format the due data using moment
            const dueDate = this.invoiceDueDate(invoice)

            //  Return formatted date
            return `<span class="${invoice.due_date >= new Date() ? 'caption' : 'red--text'}">Due: ${dueDate}</span>`
        },
        invoiceActions(invoice: Invoice): IListAction {
            //  Create our buttons
            const menu: Button[] = [
                {
                    icon: 'mdi-close-circle',
                    text: 'Cancel Scheduled Reminder',
                    color: 'red',
                    hidden: !invoice.schedule || (invoice.schedule && invoice.schedule.sent ? true : false),
                    disabledPopup: this.operator,
                    callback: () => this.cancelSchedule(invoice)
                },
                {
                    icon: 'mdi-pencil',
                    text: 'Edit Invoice',
                    color: 'primary',
                    hidden: !this.isSphere || !this.createButtonVisible,
                    disabledPopup: this.operator,
                    callback: () => this.invoiceEdit(invoice)
                }
            ]

            //  Create our IListAction
            const action: IListAction = {
                type: IListActionType.menu,
                menu: menu
            }

            //  Return our newly created actions
            return action
        },
        //  Pops the invoice form for us to create a new invoice
        invoiceCreate(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'invoice-form',
                color: 'primary',
                icon: 'mdi-receipt',
                title: 'New Invoice',
                width: 1150,
                props: { invoice: this.$store.getters.newInvoice, new: true },
                closeOnSubmit: true,
                callback: (data: InvoiceFormResponse): void => {
                    //  If openNew is true, pop new invoice form
                    if (data.openNew) this.invoiceCreate()
                }
            })
        },
        invoiceDueDate(invoice: Invoice): string {
            return moment(invoice.due_date).format('MM/DD/y')
        },
        //  Pops the invoice form for us to edit an existing invoice
        invoiceEdit(invoice: Invoice): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'invoice-form',
                color: 'primary',
                icon: 'mdi-receipt',
                title: 'Edit Invoice',
                width: 1150,
                props: { invoice: invoice, new: false },
                closeOnSubmit: true,
                callback: (data: InvoiceFormResponse): void => {
                    //  If openNew is true, pop new invoice form
                    if (data.openNew) this.invoiceCreate()
                }
            })
        },
        invoiceIcon(): IListIcon {
            //  Our icon is always just CASH MONEY BABY ;3
            const icon = 'bi-cash'

            //  Create our list icon
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'primary'
            }

            //  Return our list icon
            return listIcon
        },
        //  Searches our invoices based on a search query
        async invoicesSearch(): Promise<void> {
            //  If search text does NOT exist, our search was cleared
            if (!this.searchtext) {
                //  Disable searching
                this.searched = false
                //  Remove our current search list
                return this.searchResults = null
            }

            //  Else, assemble our search payload
            const payload = { searchtext: this.searchtext }

            //  Start our loading spinner
            this.searching = true

            //  Send the search request to the API
            const invoices = await this.$store.dispatch('invoicesSearch', payload)

            //  Stop our loading spinner
            this.searching = false

            //  Regardless of the result, set searched to true because we just searched our list
            this.searched = true

            //  Set our search results
            this.searchResults = invoices
        },
        //  Our tile for our IListTile2
        invoicesTile(): IListTile {
            return {
                actionColor: 'grey',
                actionContent: (invoice: Invoice) => this.invoiceActions(invoice),
                color: (invoice: Invoice) => (invoice.balance > 0 ? 'primary' : 'grey'),
                leftFooter: (invoice: Invoice) => `#${invoice.number}`,
                leftIcon: () => this.invoiceIcon(),
                line1: (invoice: Invoice) => window.getProps(invoice, ['person', 'name_full'], ''),
                line2: (invoice: Invoice) => `Balance: $${invoice.balance}`,
                line3: (invoice: Invoice) => this.description(invoice),
                callback: (invoice: Invoice) => this.single ? null : this.onInvoiceSelected(invoice),
                disabled: (invoice: Invoice) => invoice.balance <= 0
            }
        },
        //  Used for sending a single invoice
        async sendInvoice(type: MessageSendType): Promise<void> {
            //  Invoice to send will be our currently selected invoice
            const invoice = this.selectedInvoice

            //  Validate the single invoice
            const isValid = await this.validateInvoiceSend(invoice)

            //  If the invoice is NOT valid, do nothing (The validateInvoiceSend function will display the error message)
            if (!isValid) return

            //  Assemble the message body with template and invoice tag
            let text = this.defaultTemplate.message_body

            //  If invoice pay link is not in the template already, add it for the user
            if (!text.includes('{{ InvoicePayLink }}')) text += ' {{ InvoicePayLink }}'

            //  Create the message payload
            const msg = {
                channel_id: this.recipient.primary_channel_id,
                author_id: this.user.person && this.user.person.id,
                outbound: true,
                text: text,
                template_id: null,
                template: null,
                invoice: invoice
            }

            //  Construct our props we want to pass to our message preview window
            const props = {
                message: msg,
                person: invoice.person,
                invoiceMessages: [msg],
                type: type,
                callback: (messages: Message[]) => { 
                    //  Set our invoice message
                    this.invoiceMessage = messages[0]

                    //  Report analytics
                    Analytics.track('ManualInvoiceReminderSent', { invoice_amount: invoice.balance })
                }
            }

            //  Pop the message preview
            this.sendPreview(props)
        },
        //  Used for multi-select and sending multiple invoices to different channels
        async sendMultiple(type: MessageSendType): Promise<void> {
            //  Our containers for our messages
            const messages = []

            //  Assemble the message body with template and invoice tag
            let text = this.defaultTemplate.message_body

            //  If invoice pay link is not in the template already, add it for the user
            if (!text.includes('{{ InvoicePayLink }}')) text += ' {{ InvoicePayLink }}'

            //  Go through each invoice being sent and validate it
            for (const invoice of this.invoicesIList.multiSelected) {
                //  Create the message for each invoice
                let msg = {
                    author_id: this.user.person && this.user.person.id,
                    outbound: true,
                    text: text,
                    template_id: null,
                    template: null,
                    channel_id: invoice.person.primary_channel_id,
                    invoice: invoice
                }

                //  Validate each invoice
                let isValid = await this.validateInvoiceSend(invoice)

                //  If an invoice is not valid, then exit now
                if (!isValid) return

                //  Else, push the message onto the messages array
                messages.push(msg)
            }

            //  This is a single send if we only have 1 invoice selected
            const singleSend = this.invoicesIList.multiSelected.length === 1

            //  Construct our props we want to pass to our message preview window
            const props = {
                //  Pass along a person if only 1 invoice is being sent
                person: singleSend ? messages[0].invoice.person : null,
                //  Messages[0] is used because we have the same message for every single message being sent
                message: messages[0],
                //  Our actual array of messages being sent (includes attached invoice)
                invoiceMessages: messages,
                //  Only allow 1 schedule per invoice (if sending multiple)
                scheduleSingle: !singleSend,
                type: type,
                callback: () => {
                    //  Iterate through our invoices
                    for (let i = 0; i < this.invoicesIList.multiSelected.length; i++) {
                        //  Container for current invoice
                        let invoice = this.invoicesIList.multiSelected[i]

                        //  Report analytics
                        Analytics.track('ManualInvoiceReminderSent', { invoice_amount: invoice.balance })
                    }

                    //  Deselect multiselected items
                    this.$refs.invoicesList.multiSelectReset()
                }
            }

            //  Open message preview with our props
            this.sendPreview(props)
        },
        //  Opens a message preview to send our invoices out
        sendPreview(props: object): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Invoice Reminder Preview',
                icon: 'bi-cash',
                color: 'primary',
                component: 'message-preview',
                props: props,
                closeable: true
            })
        },
        //  Checks if the source link on our invoice is valid or not
        sourceLinkIsValid(invoice: Invoice): boolean {
            //  Check if invoice is a qbo invoice
            const isQbo = invoice.source == 'qbo'

            //  Check if invoice is a xero invoice
            const isXero = invoice.source == 'xero'

            //  If not xero or qbo, return valid. Sphere links are generated API-side
            if (!isQbo && !isXero) return true

            //  If the invoice DOES have a link, it is valid
            if (invoice.source_link) return true

            //  Else, the invoice does not have a link, open error message
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Missing Invoice Link',
                text: `Your invoice provider has not provided a link for this invoice.${isQbo ? " Make sure that the 'email' field is filled on the QuickBooks Online invoice." : ''}`,
                icon: 'mdi-alert',
                color: 'primary'
            })

            //  No link - not valid
            return false
        },
        //  Updates the phone numbers for the recipients on our invoices
        async updateInvoicePhones(_phones: Phone[], invoice: Invoice): Promise<void> {
            //  Send the request to the API
            const phones = await this.$store.dispatch('phoneNumbersPutBatch', _phones)

            //  Find the deafult phone from the updated phones array
            const defaultPhone = phones.find((phone: Phone) => phone.default)

            //  Find the invoices that need their person's phone's updated
            //  If the user selected multiple invoices attached to the same person, we have to update both seperately
            const toUpdate = this.invoicesIList.multiSelected.filter((selected: Invoice) => selected.person.name_full == invoice.person.name_full)

            //  Go through each item in the array of invoices to update
            for (const item of toUpdate) {
                //  Find thex inde of the invoice in the selected list
                const invoiceIndex = this.invoicesIList.multiSelected.findIndex((invoice: Invoice) => invoice.id == item.id)

                //  If the index is not found, do nothing and continue
                if (invoiceIndex < 0) continue;
                
                //  Set the updated phones array on the invoice's person
                this.invoicesIList.multiSelected[invoiceIndex].person.phones = phones

                //  Set the default phone on the invoice's person
                this.invoicesIList.multiSelected[invoiceIndex].person.phone_default = defaultPhone
            }

            //  If selectedInvoice matches invoice we are validating, update it also
            if (this.selectedInvoice && this.selectedInvoice.id == invoice.id) {
                //  Set phones array on invoice's person
                this.selectedInvoice.person.phones = phones

                //  Set default phone on the invoice's person
                this.selectedInvoice.person.phone_default = defaultPhone
            }

            //  Close the phone number form dialog
            this.$store.dispatch('dialogClose', 'APP')
        },
        //  Validates that the recipient can receive messages/invoice reminders
        async recipientIsValid(invoice: Invoice): Promise<boolean> {
            //  Check if invoice's person has a mobile/default phone
            const hasMobile = this.recipient.phone_default && this.recipient.phone_default.msisdn

            //  If has a mobile/default phone, valid is true
            if (hasMobile) return true

            //  Else, the phone number is invalid so have the user update these phone numbers
            await this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: `${this.recipient.name_full}'s Phone Numbers`,
                icon: 'mdi-phone-message',
                component: 'phone-numbers-form',
                overflow: 'auto',
                props: { phones: this.recipient.phones },
                callback: async (_phones: Phone[]) => { await this.updateInvoicePhones(_phones, invoice) }
            })
            
            //  Open an error message on top of the above phone numbers form to let the user know what went wrong
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'No Phone Number Found',
                text: `${this.recipient.name_full} has an invalid or missing mobile number. Please add one here.`,
                icon: 'mdi-alert',
                color: 'primary'
            })

            //  No phone - not valid
            return false
        },
        //  Validate the invoice being sent so we can see if it can actually be sent
        async validateInvoiceSend(invoice: Invoice): Promise<boolean> {
            //  Keep track of if invoice send is valid
            var valid = true

            //  Person that will receive the invoice link
            this.recipient = window.getProps(invoice, ['person'])

            //  Check for data provider invoice link
            valid = this.sourceLinkIsValid(invoice)

            //  If source link not valid, skip send
            if (!valid) return false

            //  Don't allow invoice sending if balance is 0
            valid = this.balanceIsValid(invoice)

            //  If balance not valid, skip send
            if (!valid) return false

            //  Check that the recipient has a mobile phone
            valid = await this.recipientIsValid(invoice)

            //  If recipient is not valid, skip send
            if (!valid) return false

            //  Invoice is valid
            return true
        }
    }
})